import {
  distancePointToSegment,
  localToWorld,
  pointInOrOnPolygon,
  type Vec2,
} from '@indusketch/geometry';
import { buildingArea, buildingCorners, storeyCorners } from './building.js';
import { levelElevations } from './office.js';
import type { Column, ColumnMaterial, ProjectDocument } from './types.js';

/** Usual cross sections: a HEB 240 steel column, a 40 × 40 cm concrete column. */
export const COLUMN_SIZE: Readonly<Record<ColumnMaterial, number>> = { steel: 0.24, concrete: 0.4 };

type ColumnDoc = Pick<ProjectDocument, 'buildings' | 'mezzanines'>;

/** Corners of the column's cross section in world coordinates (counter-clockwise). */
export function columnCorners(c: Pick<Column, 'center' | 'width' | 'depth' | 'rotation'>): Vec2[] {
  const hw = c.width / 2;
  const hd = c.depth / 2;
  return [
    { x: -hw, y: -hd },
    { x: hw, y: -hd },
    { x: hw, y: hd },
    { x: -hw, y: hd },
  ].map((p) => localToWorld(p, { position: c.center, rotation: c.rotation }));
}

/** Mezzanine of the hall the point lies under (or on). */
function mezzanineAt(doc: ColumnDoc, buildingId: string, p: Vec2) {
  return Object.values(doc.mezzanines).find(
    (m) => m.buildingId === buildingId && pointInOrOnPolygon(p, m.polygon, 1e-6),
  );
}

/**
 * Bottom and top of a column (absolute heights): its own height above its floor if given,
 * otherwise as high as it automatically reaches (see `autoColumnSpan`).
 */
export function columnSpan(doc: ColumnDoc, c: Column): { base: number; top: number } {
  const auto = autoColumnSpan(doc, c);
  return c.height ? { base: auto.base, top: auto.base + c.height } : auto;
}

/**
 * Automatic span: an office column fills the clear height of its storey; a hall column
 * reaches from the floor up to the mezzanine above it, or to the eaves, and one standing on a
 * mezzanine from its walking surface to the eaves.
 */
export function autoColumnSpan(doc: ColumnDoc, c: Column): { base: number; top: number } {
  const b = doc.buildings[c.buildingId];
  if (!b) return { base: 0, top: 3 };
  if (b.office) {
    const base = levelElevations(b.office)[c.level] ?? 0;
    return { base, top: base + (b.office.floors[c.level]?.clearHeight ?? 2.5) };
  }
  const m = mezzanineAt(doc, b.id, c.center);
  if (m && c.level >= 1) return { base: m.elevation, top: b.eaveHeight };
  if (m) return { base: 0, top: m.elevation - m.slabThickness };
  return { base: 0, top: b.eaveHeight };
}

/**
 * Turn that lines a new column up with its building: parallel to the nearest wall of the hall
 * or office storey (as a quarter-turn-free angle within ±45°), so it follows a skewed hall.
 */
export function columnAlignment(
  doc: Pick<ProjectDocument, 'buildings'>,
  buildingId: string,
  level: number,
  p: Vec2,
): number {
  const b = doc.buildings[buildingId];
  if (!b) return 0;
  const outline = b.office ? storeyCorners(b, level) : buildingCorners(b);
  let best = { d: Infinity, angle: b.rotation };
  outline.forEach((a, i) => {
    const c = outline[(i + 1) % outline.length] as Vec2;
    const d = distancePointToSegment(p, { a, b: c });
    if (d < best.d) best = { d, angle: Math.atan2(c.y - a.y, c.x - a.x) };
  });
  const quarter = Math.PI / 2;
  const turn = best.angle - Math.round(best.angle / quarter) * quarter;
  return Math.abs(turn) < 1e-9 ? 0 : turn;
}

/**
 * Where a column set at `p` on floor `level` belongs: the office whose storey contains the
 * point, otherwise the (smallest) hall around it; on level 1 of a hall only on a mezzanine.
 */
export function columnHost(
  doc: ColumnDoc,
  p: Vec2,
  level: number,
): { buildingId: string; level: number } | null {
  const offices = Object.values(doc.buildings)
    .filter(
      (b) =>
        b.office &&
        level < b.office.floors.length &&
        pointInOrOnPolygon(p, storeyCorners(b, level), 1e-6),
    )
    .sort((a, b) => buildingArea(a) - buildingArea(b));
  const office = offices[0];
  if (office) return { buildingId: office.id, level };
  const halls = Object.values(doc.buildings)
    .filter((b) => b.kind === 'hall' && pointInOrOnPolygon(p, buildingCorners(b), 1e-6))
    .sort((a, b) => buildingArea(a) - buildingArea(b));
  const hall = halls[0];
  if (!hall) return null;
  return { buildingId: hall.id, level: level >= 1 && mezzanineAt(doc, hall.id, p) ? 1 : 0 };
}
